import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Fade,
    Backdrop,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

const NAVY = "#1B1F3B";
const CORAL = "#FF6B6B";
const YELLOW = "#FFD93D";
const TEAL = "#06D6A0";
const CREAM = "#FFF8F0";

const EnquiryPopup = ({ open, onClose }) => {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) {
            newErrors.name = "Name is required";
        }
        if (!phone.trim()) {
            newErrors.phone = "Phone number is required";
        } else if (!/^[0-9]{10}$/.test(phone.replace(/\s/g, ""))) {
            newErrors.phone = "Enter a valid 10-digit number";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            // Here you can send data to your backend/CRM
            console.log("Enquiry submitted:", { name, phone });
            setSubmitted(true);
            setTimeout(() => {
                onClose();
                setName("");
                setPhone("");
                setSubmitted(false);
            }, 2000);
        }
    };

    return (
        <Backdrop
            open={open}
            sx={{
                zIndex: 9999,
                backgroundColor: "rgba(27, 31, 59, 0.7)",
                backdropFilter: "blur(5px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: "relative",
                        width: "90%",
                        maxWidth: 440,
                        backgroundColor: CREAM,
                        borderRadius: "24px",
                        border: `3px solid ${NAVY}`,
                        boxShadow: `8px 8px 0 ${NAVY}`,
                        overflow: "visible",
                        mx: 2,
                    }}
                >
                    {/* Decorative top bar */}
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${CORAL} 0%, ${YELLOW} 100%)`,
                            borderBottom: `3px solid ${NAVY}`,
                            borderTopRightRadius: "21px",
                            borderTopLeftRadius: "21px",
                            p: 2.5,
                            position: "relative",
                        }}
                    >
                        {/* Floating star badges */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: -15,
                                left: 20,
                                width: 45,
                                height: 45,
                                borderRadius: "50%",
                                backgroundColor: YELLOW,
                                border: `3px solid ${NAVY}`,
                                boxShadow: `3px 3px 0 ${NAVY}`,
                                display: "grid",
                                placeItems: "center",
                                animation: "wiggle 3s ease-in-out infinite",
                            }}
                        >
                            <SchoolRoundedIcon sx={{ color: NAVY, fontSize: 24 }} />
                        </Box>

                        <Box
                            sx={{
                                position: "absolute",
                                top: -10,
                                right: 15,
                                width: 35,
                                height: 35,
                                borderRadius: "50%",
                                backgroundColor: TEAL,
                                border: `2.5px solid ${NAVY}`,
                                display: "grid",
                                placeItems: "center",
                                animation: "starSpin 4s linear infinite",
                            }}
                        >
                            <StarRoundedIcon sx={{ color: "#fff", fontSize: 18 }} />
                        </Box>

                        {/* Close button */}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                backgroundColor: "#fff",
                                border: `2px solid ${NAVY}`,
                                borderRadius: "10px",
                                boxShadow: `2px 2px 0 ${NAVY}`,
                                p: 0.5,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    backgroundColor: YELLOW,
                                    transform: "translate(-1px,-1px)",
                                    boxShadow: `3px 3px 0 ${NAVY}`,
                                },
                            }}
                        >
                            <CloseRoundedIcon sx={{ color: NAVY, fontSize: 20 }} />
                        </IconButton>

                        <Typography
                            sx={{
                                fontFamily: "'Fredoka One', cursive",
                                fontSize: "1.5rem",
                                color: NAVY,
                                textAlign: "center",
                                mt: 1,
                                textShadow: "1px 1px 0 rgba(255,255,255,0.3)",
                            }}
                        >
                            {submitted ? "🎉 Thank You!" : "Enquire Now ✨"}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Space Grotesk', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                color: NAVY,
                                textAlign: "center",
                                opacity: 0.8,
                                letterSpacing: "0.05em",
                            }}
                        >
                            {submitted
                                ? "We'll get back to you soon!"
                                : "Get your child's bright future started"}
                        </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 3.5 }}>
                        {!submitted ? (
                            <form onSubmit={handleSubmit}>
                                {/* Name Field */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Space Grotesk', sans-serif",
                                            fontWeight: 800,
                                            fontSize: "0.8rem",
                                            color: NAVY,
                                            mb: 1,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                        }}
                                    >
                                        👤 Parent/Guardian Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter your full name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                        InputProps={{
                                            sx: {
                                                fontFamily: "'Space Grotesk', sans-serif",
                                                fontWeight: 600,
                                                backgroundColor: "#fff",
                                                borderRadius: "12px",
                                                border: `2.5px solid ${errors.name ? CORAL : NAVY}`,
                                                boxShadow: `3px 3px 0 ${errors.name ? CORAL : NAVY}`,
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                    transform: "translate(-1px,-1px)",
                                                    boxShadow: `4px 4px 0 ${NAVY}`,
                                                },
                                                "&.Mui-focused": {
                                                    transform: "translate(-1px,-1px)",
                                                    boxShadow: `4px 4px 0 ${TEAL}`,
                                                    border: `2.5px solid ${TEAL}`,
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    border: "none",
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Phone Field */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Space Grotesk', sans-serif",
                                            fontWeight: 800,
                                            fontSize: "0.8rem",
                                            color: NAVY,
                                            mb: 1,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                        }}
                                    >
                                        📱 Phone Number
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter 10-digit number"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        error={!!errors.phone}
                                        helperText={errors.phone}
                                        InputProps={{
                                            sx: {
                                                fontFamily: "'Space Grotesk', sans-serif",
                                                fontWeight: 600,
                                                backgroundColor: "#fff",
                                                borderRadius: "12px",
                                                border: `2.5px solid ${errors.phone ? CORAL : NAVY}`,
                                                boxShadow: `3px 3px 0 ${errors.phone ? CORAL : NAVY}`,
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                    transform: "translate(-1px,-1px)",
                                                    boxShadow: `4px 4px 0 ${NAVY}`,
                                                },
                                                "&.Mui-focused": {
                                                    transform: "translate(-1px,-1px)",
                                                    boxShadow: `4px 4px 0 ${TEAL}`,
                                                    border: `2.5px solid ${TEAL}`,
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    border: "none",
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    endIcon={<SendRoundedIcon />}
                                    sx={{
                                        py: 1.5,
                                        fontSize: "1.05rem",
                                        fontWeight: 800,
                                        fontFamily: "'Space Grotesk', sans-serif",
                                        backgroundColor: CORAL,
                                        color: "#fff",
                                        border: `2.5px solid ${NAVY}`,
                                        boxShadow: `4px 4px 0 ${NAVY}`,
                                        borderRadius: "12px",
                                        textTransform: "none",
                                        letterSpacing: "0.05em",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            backgroundColor: "#FF8080",
                                            transform: "translate(-2px,-2px)",
                                            boxShadow: `6px 6px 0 ${NAVY}`,
                                        },
                                        "&:active": {
                                            transform: "translate(0,0)",
                                            boxShadow: `2px 2px 0 ${NAVY}`,
                                        },
                                    }}
                                >
                                    Submit Enquiry
                                </Button>

                                {/* Privacy note */}
                                <Typography
                                    sx={{
                                        textAlign: "center",
                                        mt: 2,
                                        fontFamily: "'Space Grotesk', sans-serif",
                                        fontSize: "0.7rem",
                                        color: "#4A4E6D",
                                        fontWeight: 600,
                                        opacity: 0.7,
                                    }}
                                >
                                    🔒 Your information is safe with us
                                </Typography>
                            </form>
                        ) : (
                            /* Success Message */
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        mx: "auto",
                                        mb: 2.5,
                                        borderRadius: "50%",
                                        backgroundColor: TEAL,
                                        border: `3px solid ${NAVY}`,
                                        boxShadow: `4px 4px 0 ${NAVY}`,
                                        display: "grid",
                                        placeItems: "center",
                                        fontSize: "2.5rem",
                                        animation: "wiggle 2s ease-in-out infinite",
                                    }}
                                >
                                    ✅
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: "'Fredoka One', cursive",
                                        fontSize: "1.3rem",
                                        color: NAVY,
                                        mb: 1,
                                    }}
                                >
                                    Enquiry Submitted!
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Space Grotesk', sans-serif",
                                        fontWeight: 600,
                                        color: "#4A4E6D",
                                        fontSize: "0.9rem",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Our team will contact you shortly to guide you through the admission process.
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Bottom decorative dots */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                            pb: 2.5,
                        }}
                    >
                        {[CORAL, YELLOW, TEAL, CORAL, YELLOW].map((color, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: i % 3 === 0 ? "50%" : "2px",
                                    backgroundColor: color,
                                    border: `1.5px solid ${NAVY}`,
                                    animation: `floatY ${1 + i * 0.3}s ease-in-out infinite`,
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Fade>
        </Backdrop>
    );
};

export default EnquiryPopup;